import { useState } from 'react';
import { Bell, Calendar, Clock } from 'lucide-react';
import { useMutation } from '@apollo/client/react';
import { CREATE_UPDATE_REMINDER } from '../../../graphql/mutations/index.js';
import { Button } from '../../../components/ui/Button.jsx';

/**
 * @param {{
 *   userId: string,
 *   name?: string,
 *   phoneNumber?: string,
 *   symptom?: string,
 *   onComplete: (value: string) => void,
 * }} props
 */
export function YogiReminderPicker({ userId, name, phoneNumber, symptom, onComplete }) {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [createReminder, { loading }] = useMutation(CREATE_UPDATE_REMINDER);

  const handleSave = async () => {
    if (!selectedDate || !selectedTime) return;

    const dateTime = new Date(`${selectedDate}T${selectedTime}`);
    const hours = String(dateTime.getHours()).padStart(2, '0');
    const minutes = String(dateTime.getMinutes()).padStart(2, '0');
    const formattedDate = `${String(dateTime.getMonth() + 1).padStart(2, '0')}/${String(dateTime.getDate()).padStart(2, '0')}/${dateTime.getFullYear()}`;

    let messageToNextStr = `${hours}:${minutes} on ${formattedDate}`;
    if (symptom) {
      messageToNextStr += ` will help your ${symptom}.`;
    } else {
      messageToNextStr += '.';
    }

    const reminder = {
      userId,
      name: name || '',
      phoneNumber: phoneNumber || '',
      notification: messageToNextStr,
      time: {
        timeType: dateTime.toISOString(),
        index: true,
      },
    };

    try {
      await createReminder({
        variables: { userId, reminder },
      });
      onComplete(messageToNextStr);
    } catch {
      onComplete(messageToNextStr);
    }
  };

  return (
    <div className="rounded-[var(--radius-md)] border border-brand/8 bg-white p-4 shadow-[var(--shadow-sm)]">
      <div className="mb-4 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-peach/30 text-brand">
          <Bell className="h-4 w-4" strokeWidth={2} />
        </span>
        <div>
          <p className="text-sm font-semibold text-brand">Set a reminder</p>
          <p className="text-xs text-text-secondary">We&apos;ll nudge you when it&apos;s time</p>
        </div>
      </div>

      <div className="space-y-3">
        <label className="block">
          <span className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-text-secondary">
            <Calendar className="h-3.5 w-3.5" strokeWidth={2} />
            Date
          </span>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-full rounded-xl border border-brand/10 bg-surface/50 px-3 py-2.5 text-sm text-brand outline-none transition-colors focus:border-brand/30 focus:ring-2 focus:ring-brand/10 min-h-[44px]"
          />
        </label>

        <label className="block">
          <span className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-text-secondary">
            <Clock className="h-3.5 w-3.5" strokeWidth={2} />
            Time
          </span>
          <input
            type="time"
            value={selectedTime}
            onChange={(e) => setSelectedTime(e.target.value)}
            className="w-full rounded-xl border border-brand/10 bg-surface/50 px-3 py-2.5 text-sm text-brand outline-none transition-colors focus:border-brand/30 focus:ring-2 focus:ring-brand/10 min-h-[44px]"
          />
        </label>

        <Button
          variant="primary"
          onClick={handleSave}
          disabled={loading || !selectedDate || !selectedTime}
          loading={loading}
          className="w-full"
        >
          Save reminder
        </Button>
      </div>
    </div>
  );
}
